import { useEffect, useState } from 'react';

import { Box } from '@opendoor/bricks-next';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const SWIPE_THRESHOLD = 50;

export const BottomSheet = ({ isOpen, onClose, children }: BottomSheetProps) => {
  const [swipeStartY, setSwipeStartY] = useState<number | null>(null);
  const [currentTranslateY, setCurrentTranslateY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
      return undefined;
    }

    setIsAnimating(false);
    setCurrentTranslateY(0);

    const timer = setTimeout(() => setIsVisible(false), 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleTouchStart = (e: any) => {
    const touch = e.nativeEvent?.touches?.[0];
    if (touch) {
      setSwipeStartY(touch.pageY);
    }
  };

  const handleTouchMove = (e: any) => {
    if (swipeStartY === null) return;

    const touch = e.nativeEvent?.touches?.[0];
    if (!touch) return;

    const swipeDistance = touch.pageY - swipeStartY;

    // Only drag if swiping down
    if (swipeDistance > 0) {
      setCurrentTranslateY(swipeDistance);
      setIsDragging(true);
    }
  };

  const handleTouchEnd = () => {
    if (swipeStartY === null) return;

    setIsDragging(false);
    setSwipeStartY(null);

    if (currentTranslateY > SWIPE_THRESHOLD) {
      onClose();
    } else {
      setCurrentTranslateY(0);
    }
  };

  if (!isVisible) return null;

  const getTranslateY = () => {
    if (isDragging) return `${currentTranslateY}px`;
    if (isAnimating) return '0';
    return '100%';
  };

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={1000}
      style={{
        position: 'fixed',
        backgroundColor: `rgba(0, 0, 0, ${isAnimating ? 0.7 : 0})`,
        transition: 'background-color 200ms ease-out',
        touchAction: 'none',
      }}
      onTouchStart={(e: any) => {
        e.preventDefault();
        onClose();
      }}
      onTouchMove={(e: any) => e.preventDefault()}
    >
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        flexDirection="column"
        onTouchStart={(e: any) => e.stopPropagation()}
        style={{
          transform: `translateY(${getTranslateY()})`,
          transition: isDragging ? 'none' : 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)',
        }}
      >
        <Box
          width="100%"
          paddingTop="$3x"
          paddingBottom="$4x"
          flexDirection="column"
          alignItems="center"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            cursor: 'grab',
          }}
        >
          <Box borderRadius="$full" width={88} height={8} backgroundColor="$borderPrimary" />
        </Box>
        <Box
          width="100%"
          backgroundColor="$backgroundPrimary"
          borderTopLeftRadius="$16x"
          borderTopRightRadius="$16x"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
